import { useState } from "react";
import { AgentSkill, DataGridPagination } from "@/sharedTypes";
import DataTable from "@/components/Table";
import { Button, Modal, Tooltip } from "flowbite-react";
import { HiPencil, HiTrash } from "react-icons/hi";
import { toast } from "@/lib/toast";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import {
  useUpdateAgentSkillMutation,
  useDeleteAgentSkillMutation,
} from "@/api/agentApi/agentSkill";
import { getErrorMessage } from "@/utils/requestUtils";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";

interface AgentSkillsTableProps {
  data: AgentSkill[];
  pagination: DataGridPagination;
  onPageChange: (page: number) => void;
}

export default function AgentSkillsTable({
  data,
  pagination,
  onPageChange,
}: AgentSkillsTableProps) {
  const { t } = useTranslation(["agent", "common", "subcategory"]);
  const user = useCurrentUser();
  const isAdmin = isCompanyAdminUser(user);
  const [updateSkill, { isLoading: isUpdating }] =
    useUpdateAgentSkillMutation();
  const [deleteSkillMutation, { isLoading: isDeleting }] =
    useDeleteAgentSkillMutation();

  const [editSkill, setEditSkill] = useState<AgentSkill | null>(null);
  const [deleteSkill, setDeleteSkill] = useState<AgentSkill | null>(null);
  const [ratingValue, setRatingValue] = useState(0);

  const handleEditOpen = (skill: AgentSkill) => {
    setEditSkill(skill);
    setRatingValue(skill.skill_rate || 0);
  };

  const handleDeleteOpen = (skill: AgentSkill) => {
    setDeleteSkill(skill);
  };

  const handleUpdateRating = async () => {
    const skillId = editSkill?.id || editSkill?.agent_skill_id;
    if (!editSkill || !skillId) return;

    try {
      await updateSkill({
        id: skillId,
        data: {
          subcategory_id: editSkill.subcategory_id,
          skill_rate: ratingValue,
        },
      }).unwrap();
      toast.success(t("agent:skillUpdated"));
      setEditSkill(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDelete = async () => {
    const skillId = deleteSkill?.id || deleteSkill?.agent_skill_id;
    if (!deleteSkill || !skillId) return;

    try {
      await deleteSkillMutation(skillId).unwrap();
      toast.success(t("agent:skillDeleted"));
      setDeleteSkill(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const columns = [
    {
      key: "subcategory",
      label: t("agent:subcategory"),
      render: (row: AgentSkill) => (
        <span>{row.subcategory.subcategory_name}</span>
      ),
    },
    {
      key: "skill_rate",
      label: t("subcategory:skill_rate"),
      render: (row: AgentSkill) => {
        const rating = row.skill_rate || 0;
        return (
          <div className="flex text-yellow-400">
            {[1, 2, 3, 4, 5].map((star) => (
              <span key={star} className="text-lg">
                {star <= rating ? "★" : "☆"}
              </span>
            ))}
          </div>
        );
      },
    },
    ...(isAdmin
      ? [
          {
            key: "actions",
            label: t("subcategory:actions"),
            render: (row: AgentSkill) => (
              <div className="flex gap-2">
                <Tooltip content={t("common:edit")}>
                  <Button
                    size="xs"
                    color="light"
                    onClick={() => handleEditOpen(row)}
                  >
                    <HiPencil className="h-4 w-4 text-green-600 dark:text-green-400" />
                  </Button>
                </Tooltip>
                <Tooltip content={t("common:delete")}>
                  <Button
                    size="xs"
                    color="light"
                    onClick={() => handleDeleteOpen(row)}
                  >
                    <HiTrash className="h-4 w-4 text-red-600 dark:text-red-400" />
                  </Button>
                </Tooltip>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={data}
        pagination={pagination}
        onPageChange={onPageChange}
      />

      {/* Edit Modal */}
      <Modal show={!!editSkill} onClose={() => setEditSkill(null)}>
        <Modal.Header>{t("subcategory:update_skill_rate")}</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col items-center gap-4">
            <p className="text-gray-500 dark:text-gray-400">
              {t("agent:selectRatingFor", {
                name: editSkill?.subcategory?.subcategory_name,
              })}
            </p>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRatingValue(star)}
                  className={`text-3xl focus:outline-none transition-colors ${
                    star <= ratingValue
                      ? "text-yellow-400"
                      : "text-gray-300 dark:text-gray-600"
                  }`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => setEditSkill(null)}>
            {t("common:cancel")}
          </Button>
          <Button
            color="blue"
            onClick={handleUpdateRating}
            isProcessing={isUpdating}
          >
            {t("common:save")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation */}
      {deleteSkill && (
        <ConfirmationDialog
          title={t("agent:deleteSkill")}
          body={t("agent:deleteSkillConfirm", {
            name: deleteSkill.subcategory.subcategory_name,
          })}
          onConfirm={handleDelete}
          onCancel={() => setDeleteSkill(null)}
          isLoading={isDeleting}
        />
      )}
    </>
  );
}
